import { sortBy, uniq } from "lodash";
import { createSelector } from "reselect";

import { EContractStatus } from "constants/eContract";
import {
  FieldToGroupLabelDescriptionMap,
  SaleSearchField,
} from "constants/globalSearch";
import { SaleStatusLabels } from "constants/sale";

import { resolveSaleStatus } from "lib/saleStatus";

import {
  selectEffectiveEContractStatusBySaleIdLookup,
  selectEffectiveSaleTitleBySaleIdLookup,
} from "../sales";
import { getActiveRole, getSales, getSpecies, getSaleStatuses } from "../root";
import { selectSaleRoleNameByUserId } from "../saleRoles";

const E_CONTRACT_STATUS_ORDER = Object.values(EContractStatus);

const createSaleOption = (labelField, valueField, value) => ({
  label: labelField,
  value: {
    field: valueField,
    value,
  },
});

const createSaleSearchSelector = (valueField, mapFunction) =>
  createSelector([getSales], sales => {
    return sortBy(
      uniq(Object.values(sales).map(mapFunction)).map(option =>
        createSaleOption(option, valueField, option),
      ),
      "label",
    );
  });

const selectSaleSearchSaleIds = createSaleSearchSelector(
  SaleSearchField.SaleId,
  sale => sale.livestocksale_id,
);

const selectSaleSearchSaleDate = createSaleSearchSelector(
  SaleSearchField.Date,
  sale => sale.date,
);

const selectSaleSearchSaleType = createSaleSearchSelector(
  SaleSearchField.Type,
  sale => sale.sale_type,
);

const selectSaleSearchSaleSaleyard = createSaleSearchSelector(
  SaleSearchField.Saleyard,
  sale => sale.saleyard_name,
);

const selectSaleSearchSaleEContractStatus = createSelector(
  [getSales, selectEffectiveEContractStatusBySaleIdLookup],
  (sales, effectiveEContractStatusBySaleIdLookup) => {
    const statuses = uniq(
      Object.values(sales)
        .map(
          sale => effectiveEContractStatusBySaleIdLookup[sale.livestocksale_id],
        )
        .filter(Boolean),
    );
    return sortBy(
      statuses.map(status =>
        createSaleOption(status, SaleSearchField.EContractStatus, status),
      ),
      option => E_CONTRACT_STATUS_ORDER.indexOf(option.value.value),
    );
  },
);

const selectSaleSearchSaleTitle = createSelector(
  [getSales, selectEffectiveSaleTitleBySaleIdLookup],
  (sales, effectiveTitleBySaleId) => {
    const titles = uniq(
      Object.values(sales).map(
        sale => effectiveTitleBySaleId[sale.livestocksale_id],
      ),
    ).filter(Boolean);
    return sortBy(
      titles.map(title =>
        createSaleOption(title, SaleSearchField.Title, title),
      ),
      "label",
    );
  },
);

const selectSaleSearchSaleSpecies = createSelector(
  [getSpecies, getSales],
  (species, sales) => {
    return sortBy(
      uniq(Object.values(sales).map(sale => sale.species_id)).map(
        saleSpecies => {
          const speciesName = species[saleSpecies]?.name || "";
          return createSaleOption(
            speciesName,
            SaleSearchField.Species,
            saleSpecies,
          );
        },
      ),
      "label",
    );
  },
);

const selectSaleSearchSaleStatus = createSelector(
  [getSales, getSaleStatuses, getActiveRole],
  (sales, saleStatusesById, role) => {
    // Grab the custom statuses - we'll only see these
    // if we have them in our catalogue of statuses (literally - am I an agent).

    const customStatusOptions = new Set();
    const baseStatusOptions = new Set();

    Object.values(sales).forEach(sale => {
      const { archetype, customStatus } = resolveSaleStatus(
        sale,
        role,
        saleStatusesById,
      );
      if (customStatus) {
        customStatusOptions.add(customStatus.id);
      } else {
        baseStatusOptions.add(archetype);
      }
    });
    const customOptionsWithLabel = [...customStatusOptions].map(saleStatusId =>
      createSaleOption(
        saleStatusesById[saleStatusId].name,
        SaleSearchField.Status,
        saleStatusId,
      ),
    );
    const optionsWithLabel = [...baseStatusOptions].map(option =>
      createSaleOption(
        SaleStatusLabels[option],
        SaleSearchField.Status,
        option,
      ),
    );

    return sortBy(customOptionsWithLabel.concat(optionsWithLabel), "label");
  },
);

// Distinct managers / assignees across the loaded sales, as {label: name,
// value: {field, value: userId}} options for the search dropdown. Names are
// resolved from the agent directory, like the dashboard chips.
const createSaleRoleSearchSelector = (idField, field) =>
  createSelector(
    [getSales, selectSaleRoleNameByUserId],
    (sales, nameByUserId) => {
      const userIds = new Set();
      Object.values(sales).forEach(sale => {
        (sale.sale_attributes?.[idField] || []).forEach(id => userIds.add(id));
      });
      return sortBy(
        [...userIds]
          .filter(id => nameByUserId[id])
          .map(id => createSaleOption(nameByUserId[id], field, id)),
        "label",
      );
    },
  );

const selectSaleSearchManagers = createSaleRoleSearchSelector(
  "managerIds",
  SaleSearchField.Managers,
);

const selectSaleSearchAssignees = createSaleRoleSearchSelector(
  "assigneeIds",
  SaleSearchField.Assignees,
);

export const selectSaleSearchOptions = createSelector(
  [
    selectSaleSearchSaleDate,
    selectSaleSearchSaleIds,
    selectSaleSearchSaleSaleyard,
    selectSaleSearchSaleSpecies,
    selectSaleSearchSaleStatus,
    selectSaleSearchSaleTitle,
    selectSaleSearchSaleType,
    selectSaleSearchManagers,
    selectSaleSearchAssignees,
    selectSaleSearchSaleEContractStatus,
  ],
  (
    saleDateOptions,
    saleIdOptions,
    saleSaleyardOptions,
    saleSpeciesOptions,
    saleStatusOptions,
    salesTitleOptions,
    saleTypeOptions,
    saleManagerOptions,
    saleAssigneeOptions,
    saleEContractStatusOptions,
  ) => {
    return [
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchField.Date],
        field: SaleSearchField.Date,
        options: saleDateOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchField.SaleId],
        field: SaleSearchField.SaleId,
        options: saleIdOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchField.Saleyard],
        field: SaleSearchField.Saleyard,
        options: saleSaleyardOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchField.Species],
        field: SaleSearchField.Species,
        options: saleSpeciesOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchField.Status],
        field: SaleSearchField.Status,
        options: saleStatusOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchField.Title],
        field: SaleSearchField.Title,
        options: salesTitleOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchField.Type],
        field: SaleSearchField.Type,
        options: saleTypeOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchField.Managers],
        field: SaleSearchField.Managers,
        options: saleManagerOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchField.Assignees],
        field: SaleSearchField.Assignees,
        options: saleAssigneeOptions,
      },
      {
        label: FieldToGroupLabelDescriptionMap[SaleSearchField.EContractStatus],
        field: SaleSearchField.EContractStatus,
        options: saleEContractStatusOptions,
      },
    ];
  },
);
